import React from 'react';

import {
	Box,
	Container,
	Stack,
	Typography,
	type SxProps,
	type Theme,
	SvgIcon,
} from '@mui/material';

import RouteLink from '@ivy/components/atoms/RouteLink';

interface Resource {
	url: string;
	label: string;
}

interface ResourceListProps {
	title: string;
	resources: Resource[];
	sx?: SxProps<Theme>;
}

const ResourceList = ({ title, resources, sx }: ResourceListProps) => {
	return (
		<Stack spacing={2} sx={sx}>
			<Typography variant='h4' component='h3'>
				{title}
			</Typography>
			{resources.map((resource) => (
				<Box
					key={resource.label}
					sx={{
						display: 'flex',
						alignItems: 'start',
					}}
				>
					<SvgIcon
						width='14'
						height='12'
						viewBox='0 0 14 12'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
						sx={{
							position: 'relative',
							top: '3px',
							fontSize: '16px',
							mr: 1,
						}}
					>
						<path d='M0 1H14' stroke='#005EB8' strokeWidth='2' />
						<path d='M0 6H13' stroke='#005EB8' strokeWidth='2' />
						<path d='M0 11H10' stroke='#005EB8' strokeWidth='2' />
					</SvgIcon>
					<Box>
						<RouteLink
							to={resource.url}
							openInNewTab
							underline='hover'
							sx={{
								color: 'text.icon',
								fontWeight: 'bold',
							}}
						>
							{resource.label}
						</RouteLink>
					</Box>
				</Box>
			))}
		</Stack>
	);
};

const AcepResources = () => {
	return (
		<Box
			sx={{
				position: 'relative',
				py: {
					xs: 10,
					md: 25,
				},
			}}
		>
			<Box
				component='svg'
				width='1921'
				height='72'
				viewBox='0 0 1921 72'
				fill='none'
				xmlns='http://www.w3.org/2000/svg'
				preserveAspectRatio='none'
				sx={{
					// Force an overlap
					mt: '-1px',
					position: 'absolute',
					top: 0,
					width: '100vw',
				}}
			>
				<path
					d='M1920 0.474854L0.00378418 0.470703V0.474854C0.00378418 0.474854 329.708 71.0044 607.856 71.0044C886.004 71.0044 1000.76 13.7368 1472.5 0.474854C1772.5 0.474854 1920 45.9027 1920 45.9027L1920 0.474854Z'
					fill='#005EB8'
				/>
			</Box>
			<Container
				maxWidth='xl'
				sx={{
					// Extra margin so account for wave taking up space at top of this page
					mt: 5,
					display: 'flex',
				}}
			>
				<Box
					sx={{
						flex: '1 1 auto',
					}}
				>
					<Typography variant='h2' mb={15}>
						Resources in ACEP's Library
					</Typography>
					<Stack
						direction={{
							xs: 'column',
							md: 'row',
						}}
						spacing={8}
					>
						<ResourceList
							title='Contracts'
							resources={[
								{
									label:
										'A Checklist to Help You Negotiate the Best Employment Contract',
									url: 'https://www.acep.org/life-as-a-physician/career-center/negotiating-the-best-employment-contract',
								},
								{
									label:
										'A Contract Toolkit with Legal, Insurance, and Contract Concerns',
									url: 'https://www.acep.org/careertransitionsupport',
								},
								{
									label: 'Navigating Legal Concerns Webinar',
									url: 'https://www.acep.org/life-as-a-physician/career-center/Contract-toolkit/navigating-legal-concerns-during-your-app-transition',
								},
								{
									label: 'Discounted Employment Contract Review Services',
									url: 'https://www.acep.org/acep-membership/membership/benefits-of-membership/member-benefits/benefits-articles/resolve',
								},
							]}
							sx={{
								flexBasis: {
									md: '50%',
								},
							}}
						/>
						<ResourceList
							title='Policies'
							resources={[
								{
									label: 'Considering Compensation Arrangements',
									url: 'https://www.acep.org/patient-care/policy-statements/compensation-arrangements-for-emergency-physicians',
								},
								{
									label: 'Understanding Your Rights as an EP',
									url: 'https://www.acep.org/patient-care/policy-statements/emergency-physician-rights-and-responsibilities',
								},
								{
									label: 'Advocating for Compensation Transparency',
									url: 'https://www.acep.org/patient-care/policy-statements/emergency-physician-compensation-transparency',
								},
								{
									label: 'All ACEP Employment Policies',
									url: 'https://www.acep.org/life-as-a-physician/career-center/#policies',
								},
							]}
							sx={{
								flexBasis: {
									md: '50%',
								},
							}}
						/>
					</Stack>
				</Box>
				<Box
					sx={{
						flex: '0 1 488px',
						ml: 16,
						display: {
							xs: 'none',
							lg: 'initial',
						},
					}}
				>
					<Box
						component='img'
						src='https://assets.ivyclinicians.io/images/acep-landing-resources.png'
						sx={{
							borderRadius: (theme) =>
								`${
									theme.cardShape?.borderRadius || theme.shape.borderRadius
								}px`,
							height: '100%',
							width: '100%',
							objectFit: 'cover',
						}}
					/>
				</Box>
			</Container>
		</Box>
	);
};

export default AcepResources;
